import {
  POWER_PLUG_IDS,
  type PowerPlugId,
  isPowerPlugAvailableInRegion,
} from 'utils/powerPlugAvailability';

/**
 * Figma chip order (2832:103219). Filtered per region before render; the
 * funnel shows as many as fit two rows and collapses the rest into "+ N more".
 */
export const MAX_CHIP_KEYS = [
  'cardio',
  'afib',
  'cycle',
  'respiratory',
  'jade',
  'healthex',
  'lesmills',
  'migraine',
  'tesla',
] as const;

export type MaxChipKey = (typeof MAX_CHIP_KEYS)[number];

/** Chips shown before the client measures the row (the 361px Figma frame fits four). */
export const MAX_VISIBLE_CHIP_FALLBACK = 4;

/**
 * Region rules for the MAX chip row (this repo only):
 * - AFib / Cardio Adaptability: mutually exclusive via `powerPlugAvailability`
 *   (`AfibCountryCodes`). US/IN/CA/JP show Cardio; GB/EU/AU/AE/… show AFib.
 * - Cycle: always shown. Label is Pro vs Plus (`CNO_PRO_COUNTRIES` in
 *   `useIsCnOPlus`) — not a hide/show.
 * - Health EX: US only (HealthEx reads US medical records).
 * - Migraine: not yet approved for MAX. Hidden on production until
 *   `MIGRAINE_APPROVED` flips; testing builds show it, in the launch countries only.
 * - Tesla, Respiratory, Les Mills, Jade: no country list on the site (Tesla
 *   Sync is always-on in `powerPlugAvailability`; Respiratory is in every
 *   Shopify store map; Les Mills has no geo gate).
 */
const CHIP_TO_POWER_PLUG: Partial<Record<MaxChipKey, PowerPlugId>> = {
  cardio: POWER_PLUG_IDS.CARDIO_ADAPTABILITY,
  tesla: POWER_PLUG_IDS.TESLA_SYNC,
  afib: POWER_PLUG_IDS.AFIB_DETECTION,
};

const HEALTHEX_COUNTRY_CODES = new Set(['US']);

/** Flip once Migraine Insights is approved to ship inside MAX. */
const MIGRAINE_APPROVED = false;
const MIGRAINE_COUNTRY_CODES = new Set([
  'CA',
  'DE',
  'ES',
  'FR',
  'CZ',
  'PL',
  'NL',
  'IT',
  'AT',
  'BE',
  'SE',
  'CH',
  'GB',
  'AU',
]);

const isMigraineLive = (): boolean =>
  MIGRAINE_APPROVED || process.env['NEXT_PUBLIC_ENVIRONMENT'] !== 'PRODUCTION';

const regionCode = (region?: string | null): string =>
  (region || '').toUpperCase().trim();

export const isMaxChipAvailable = (
  chip: MaxChipKey,
  region?: string | null,
): boolean => {
  if (chip === 'healthex')
    return HEALTHEX_COUNTRY_CODES.has(regionCode(region));
  if (chip === 'migraine') {
    return isMigraineLive() && MIGRAINE_COUNTRY_CODES.has(regionCode(region));
  }
  const plugId = CHIP_TO_POWER_PLUG[chip];
  if (!plugId) return true;
  return isPowerPlugAvailableInRegion(region, plugId);
};

export const getMaxChipsForRegion = (region?: string | null): MaxChipKey[] =>
  MAX_CHIP_KEYS.filter((chip) => isMaxChipAvailable(chip, region));

/** Jade is a MAX benefit, not a PowerPlug — omit it from "N PowerPlugs" copy. */
export const getMaxPowerPlugCount = (region?: string | null): number =>
  getMaxChipsForRegion(region).filter((chip) => chip !== 'jade').length;

export const getMaxCycleLabelKey = (
  isCnOPlus: boolean,
): 'cycle' | 'cyclePlus' => (isCnOPlus ? 'cyclePlus' : 'cycle');
